import React, { useMemo } from 'react'
import type { ComponentConfig } from '@puckeditor/core'
import { WithLayout, withLayout } from '../Layout'
import { ColorField } from '../../fields/ColorField'
import { sanitizeSvg, normalizeSvg } from './sanitizeSvg'

export type SvgIconProps = WithLayout<{
  svg?: string
  color?: string
  size?: string
  forceCurrentColor?: boolean
  label?: string
}>

const PLACEHOLDER_STYLE: React.CSSProperties = {
  padding: 12,
  fontSize: 12,
  textAlign: 'center',
  color: '#999',
  border: '1px dashed #ddd',
  borderRadius: 4,
}

const SvgIconRender = ({
  svg,
  color,
  size,
  forceCurrentColor,
  label,
}: SvgIconProps) => {
  const html = useMemo(
    () => normalizeSvg(sanitizeSvg(svg), forceCurrentColor !== false),
    [svg, forceCurrentColor],
  )

  if (!html) {
    return <div style={PLACEHOLDER_STYLE}>Paste SVG markup to display an icon</div>
  }

  const box = size || '24px'

  return (
    <span
      // `color` is what the SVG inherits once its fills are currentColor.
      style={{
        display: 'inline-flex',
        width: box,
        height: box,
        color: color || undefined,
        lineHeight: 0,
      }}
      {...(label
        ? { role: 'img', 'aria-label': label }
        : { 'aria-hidden': true })}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

const SvgIconInternal: ComponentConfig<SvgIconProps> = {
  label: 'SVG Icon',
  fields: {
    svg: {
      type: 'textarea',
      label: 'SVG Markup',
      placeholder: '<svg viewBox="0 0 24 24">…</svg>',
    },
    color: {
      type: 'custom',
      label: 'Color',
      render: (props: React.ComponentProps<typeof ColorField>) => (
        <ColorField {...props} clearable />
      ),
    },
    size: { type: 'text', label: 'Size', placeholder: 'e.g. 24px, 2rem' },
    forceCurrentColor: {
      type: 'radio',
      label: 'Use Color Field',
      options: [
        { label: 'Yes', value: true },
        { label: 'No (keep SVG colours)', value: false },
      ],
    },
    label: {
      type: 'text',
      label: 'Accessible Label (blank hides it from screen readers)',
      placeholder: 'Calendar',
    },
  },
  defaultProps: {
    svg: '',
    color: '',
    size: '24px',
    forceCurrentColor: true,
    label: '',
  },
  render: SvgIconRender,
}

export const PuckSvgIcon = withLayout(SvgIconInternal)
